import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { podcastApi } from '../services/api';
import CustomVideoPlayer from '../components/CustomVideoPlayer';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Share2, Mic, Radio, Calendar, Clock, Globe, Zap, Trophy, Lock, CreditCard } from 'lucide-react';
import SocialShareDialog from '../components/SocialShareDialog';
import { SINGLE_EPISODE_PRICE } from '../config/pricing';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const PodcastPrivatePage = () => {
  const { podcastId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [podcast, setPodcast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(null);
  const shareUrl = podcast ? `${window.location.origin}/share/${podcast.shareId}` : '';
  const isPreview = !!podcast && !podcast.isPaid;

  useEffect(() => {
    const fetchPodcast = async () => {
      try {
        setLoading(true);
        const res = await podcastApi.getMyPodcasts();
        const foundPodcast = res.data.find(p => p._id === podcastId);
        
        if (!foundPodcast) {
          setError('Podcast not found or you do not have access to it.');
          return;
        }
        
        setPodcast(foundPodcast);
        setError(null);
      } catch (e) {
        console.error('Error fetching podcast:', e);
        if (e.response?.status === 401) {
          setError('Please log in to view this podcast.');
        } else {
          setError('Failed to load podcast. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchPodcast();
    } else {
      setError('Please log in to view this podcast.');
      setLoading(false);
    }
  }, [podcastId, user]);

  // Poll for video availability separately — no podcast in deps to avoid re-fetch loop
  useEffect(() => {
    if (!podcast || podcast.status !== 'completed' || podcast.videoFilePath) return;

    const pollForVideo = setInterval(async () => {
      try {
        const res = await podcastApi.getMyPodcasts();
        const updatedPodcast = res.data.find(p => p._id === podcastId);
        if (updatedPodcast?.videoFilePath) {
          setPodcast(updatedPodcast);
          clearInterval(pollForVideo);
        }
      } catch (e) {
        console.error('Error polling for video:', e);
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(pollForVideo);
  }, [podcast?.status, podcast?.videoFilePath, podcastId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle publish/unpublish
  const handleTogglePublish = async () => {
    try {
      if (podcast.isPublic) {
        // Unpublish
        await podcastApi.unpublishPodcast(podcast._id);
        setPodcast(prev => ({ ...prev, isPublic: false }));
      } else {
        // Publish
        const res = await podcastApi.publishPodcast(podcast._id);
        setPodcast(prev => ({ ...prev, isPublic: true, shareId: res.shareId }));
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      alert('Failed to update sharing status. Please try again.');
    }
  };

  // Handle sharing
  const handleShare = async () => {
    // Use server-rendered share URL for rich social previews (OG/Twitter meta)
    const publicUrl = `${window.location.origin}/share/${podcast.shareId}`;
    const mobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    try {
      if (navigator.share && mobile) {
        await navigator.share({
          title: podcast.title,
          url: publicUrl,
        });
      } else {
        setShareOpen(true);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      setShareOpen(true);
    }
  };

  // Unlock the full episode by spending a credit (or send the user to buy credits).
  const handleUnlock = async () => {
    if (unlocking) return;
    setUnlockError(null);

    if ((user?.credits ?? 0) < 1) {
      // No credits — send them to the pricing section to purchase.
      window.location.href = '/#pricing';
      return;
    }

    try {
      setUnlocking(true);
      const res = await podcastApi.unlockPodcast(podcast._id);
      if (res?.isPaid) {
        setPodcast(prev => ({ ...prev, isPaid: true }));
        await refreshUser();
      }
    } catch (e) {
      console.error('Error unlocking podcast:', e);
      if (e.response?.status === 402) {
        window.location.href = '/#pricing';
        return;
      }
      setUnlockError(e.response?.data?.error || 'Failed to unlock. Please try again.');
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-royal-blue to-blue-400 flex items-center justify-center mx-auto mb-4 shadow-xl">
            <Radio className="h-8 w-8 text-white animate-pulse" />
          </div>
          <p className="text-gray-400 font-medium">Loading your GameCast...</p>
        </div>
      </div>
    );
  }

  if (error || !podcast) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-full bg-red-900/40 flex items-center justify-center mx-auto mb-4">
            <Mic className="h-7 w-7 text-red-400" />
          </div>
          <h1 className="text-xl font-black text-white mb-2">Couldn't Load Podcast</h1>
          <p className="text-gray-400 mb-6 text-sm">{error || 'Unable to load this podcast.'}</p>
          <Link className="inline-flex items-center gap-2 text-royal-blue hover:underline font-semibold text-sm" to="/my-podcasts">
            <ArrowLeft className="h-4 w-4" />Back to My Podcasts
          </Link>
        </div>
      </div>
    );
  }

  if (podcast.status !== 'completed' || !podcast.videoFilePath) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 flex items-center justify-center mx-auto mb-4 shadow-xl">
            <Radio className="h-8 w-8 text-white animate-pulse" />
          </div>
          <h1 className="text-xl font-black text-white mb-2">Still Generating…</h1>
          <p className="text-gray-500 mb-6 text-sm">
            {podcast.status === 'generating'
              ? 'Your podcast is being produced. Check back in a few minutes.'
              : 'Your GameCast audio is ready — finalizing the video player now…'}
          </p>
          <Link className="inline-flex items-center gap-2 text-royal-blue hover:underline font-semibold text-sm" to="/my-podcasts">
            <ArrowLeft className="h-4 w-4" />Back to My Podcasts
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800">

      {/* ── Dark hero header ── */}
      <div className="bg-gradient-to-r from-gray-900 via-blue-950 to-gray-900 border-b-4 border-yellow-400">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back nav */}
          <button
            onClick={() => navigate('/my-podcasts')}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />Back to My Podcasts
          </button>

          {/* Title area */}
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-yellow-400 flex items-center justify-center shadow-lg shrink-0 mt-1">
              <Mic className="h-7 w-7 text-gray-900" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-xs font-bold text-yellow-400 uppercase tracking-widest">GameCast</span>
                {isPreview ? (
                  <span className="inline-flex items-center gap-1 text-xs font-bold bg-yellow-400/20 text-yellow-300 border border-yellow-400/30 rounded-full px-2 py-0.5">
                    <Lock className="h-3 w-3" />Preview
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-bold bg-electric-green/20 text-electric-green border border-electric-green/30 rounded-full px-2 py-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-electric-green" />Ready
                  </span>
                )}
                {podcast.isPublic && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-full px-2 py-0.5">
                    <Globe className="h-3 w-3" />Public
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                {podcast.opponentName ? `${podcast.teamName} vs. ${podcast.opponentName}` : podcast.title}
              </h1>
              {podcast.audioTitle && (
                <p className="text-sm text-blue-300 mt-1 italic">{podcast.audioTitle}</p>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-400">
                {podcast.teamName && <span className="flex items-center gap-1">🏆 {podcast.teamName}</span>}
                {podcast.createdAt && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />{new Date(podcast.createdAt).toLocaleDateString()}
                  </span>
                )}
                {podcast.duration && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {Math.floor(podcast.duration / 60)}:{String(podcast.duration % 60).padStart(2, '0')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">

          {/* ── Player column (2/3) ── */}
          <div className="lg:col-span-2 space-y-5">
            {/* Video / audio player */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 shadow-xl overflow-hidden">
              <div className="h-1.5 w-full bg-gradient-to-r from-royal-blue to-electric-green" />
              <div className="p-5 bg-gray-900/60">
                <CustomVideoPlayer
                  src={`${API_BASE_URL}/api/podcasts/stream-mp4/${podcast._id}`}
                  fallbackAudioSrc={`${API_BASE_URL}${podcast.audioUrl}`}
                  title={podcast.title}
                  downloadHref={`${API_BASE_URL}/api/podcasts/download-mp4/${podcast._id}`}
                  isPreview={isPreview}
                  onUnlock={handleUnlock}
                  credits={user?.credits ?? 0}
                />
              </div>
            </div>

            {/* Preview paywall banner (below the player) */}
            {isPreview && (
              <div className="bg-gradient-to-br from-yellow-500/15 to-orange-500/10 border border-yellow-400/30 rounded-2xl px-5 py-4 flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-yellow-400 flex items-center justify-center shrink-0">
                  <Lock className="h-4 w-4 text-gray-900" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-black text-yellow-300 mb-1">You're hearing the free preview</p>
                  <p className="text-xs text-gray-300 mb-3">Unlock the full episode to hear the entire podcast and share it with fans</p>
                  {unlockError && <p className="text-xs text-red-400 mb-2">{unlockError}</p>}
                  <button
                    onClick={handleUnlock}
                    disabled={unlocking}
                    className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-gray-900 font-black py-2 px-5 rounded-xl transition-colors shadow-md text-sm"
                  >
                    {unlocking ? (
                      <>Unlocking…</>
                    ) : (user?.credits ?? 0) >= 1 ? (
                      <><Lock className="h-4 w-4" />Unlock Full Episode — Use 1 Credit</>
                    ) : (
                      <><CreditCard className="h-4 w-4" />Unlock Full Episode — {SINGLE_EPISODE_PRICE}</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Public link banner – only when public */}
            {podcast.isPublic && podcast.shareId && (
              <div className="bg-green-900/30 border border-green-500/30 rounded-2xl px-5 py-4 flex items-start gap-3">
                <Globe className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-green-300 mb-1">This GameCast is public</p>
                  <Link
                    to={`/p/${podcast.shareId}`}
                    className="text-xs text-green-400 hover:text-green-300 underline break-all font-medium"
                  >
                    {window.location.origin}/p/{podcast.shareId}
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* ── Sidebar (1/3) ── */}
          <div className="space-y-5">

            {/* Unlock card — shown for preview-only podcasts */}
            {isPreview && (
              <div className="bg-gradient-to-br from-yellow-500/15 to-orange-500/10 rounded-2xl border border-yellow-400/30 shadow-xl p-5">
                <h3 className="font-black text-yellow-300 text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Lock className="h-4 w-4" />Unlock Full Episode
                </h3>
                <p className="text-xs text-gray-300 mb-4">
                  Unlock the full episode to hear the entire podcast and share it with fans
                </p>
                <div className="flex items-center justify-between text-xs text-gray-300 mb-4">
                  <span>Your credits</span>
                  <span className={`font-bold ${(user?.credits ?? 0) > 0 ? 'text-electric-green' : 'text-red-400'}`}>{user?.credits ?? 0}</span>
                </div>
                {unlockError && <p className="text-xs text-red-400 mb-2">{unlockError}</p>}
                <button
                  onClick={handleUnlock}
                  disabled={unlocking}
                  className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-gray-900 font-black py-2.5 rounded-xl transition-colors shadow-md text-sm"
                >
                  {unlocking ? (
                    <>Unlocking…</>
                  ) : (user?.credits ?? 0) >= 1 ? (
                    <><Lock className="h-4 w-4" />Unlock — Use 1 Credit</>
                  ) : (
                    <><CreditCard className="h-4 w-4" />Unlock — {SINGLE_EPISODE_PRICE}</>
                  )}
                </button>
              </div>
            )}

            {/* Share controls card — only for unlocked episodes */}
            {!isPreview && (
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 shadow-xl p-5">
              <h3 className="font-black text-gray-200 text-sm uppercase tracking-wide mb-4 flex items-center gap-2">
                <Share2 className="h-4 w-4 text-blue-400" />Sharing
              </h3>

              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-gray-300">
                  {podcast.isPublic ? 'Public — anyone can listen' : 'Private — only you'}
                </span>
                <button
                  onClick={handleTogglePublish}
                  title={podcast.isPublic ? 'Make private' : 'Make public'}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-royal-blue/30 ${
                    podcast.isPublic ? 'bg-electric-green' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${podcast.isPublic ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {podcast.isPublic && podcast.shareId ? (
                <button
                  onClick={handleShare}
                  className="w-full inline-flex items-center justify-center gap-2 bg-royal-blue hover:bg-blue-800 text-white font-bold py-2.5 rounded-xl transition-colors shadow-md text-sm"
                >
                  <Share2 className="h-4 w-4" />Share This GameCast
                </button>
              ) : (
                <p className="text-xs text-gray-500 text-center">Enable sharing above to get a public link.</p>
              )}
            </div>
            )}

            {/* Create another card */}
            <div className="bg-gradient-to-br from-gray-900 to-blue-950 rounded-2xl p-5 shadow-xl border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="h-5 w-5 text-yellow-400" />
                <h3 className="font-black text-white text-sm">Ready for the next game?</h3>
              </div>
              <p className="text-gray-400 text-xs mb-4">Turn any box score into an AI-powered GameCast in minutes.</p>
              <Link
                to="/create"
                className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-black py-2.5 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl text-sm"
              >
                <Zap className="h-4 w-4" />Create Another
              </Link>
            </div>

            {/* Quick tips card */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 shadow-xl p-5">
              <h3 className="font-black text-gray-200 text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
                <Radio className="h-4 w-4 text-blue-400" />Quick Tips
              </h3>
              <ul className="space-y-2.5">
                {[
                  { icon: '🔗', text: 'Toggle public to get a shareable link.' },
                  { icon: '📲', text: 'Share your link so fans can listen instantly.' },
                  { icon: '🏆', text: 'Send the link to parents, players, and coaches!' },
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-xs text-gray-300">
                    <span className="shrink-0 w-6 h-6 bg-white/10 rounded-lg flex items-center justify-center">{tip.icon}</span>
                    {tip.text}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <SocialShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shareUrl={shareUrl}
        title={podcast.title}
        text=''
      />
    </div>
  );
};

export default PodcastPrivatePage;
